// Radha — CRM, enhanced with mechanisms ported from the Nikunj CRM.
//
// Command KPIs, a Kanban board, an idle-adjusted win-probability chip with its
// derivation on hover, and a deal drawer with the activity timeline + a
// reason-logged stage move. The forecasting recomputes on Rasvyn's own book;
// none of THB's data, vocabulary, POD logic or coefficients cross over.
import React, { useMemo, useState } from "react";

import { useCrmDeals } from "../hooks/useBatchModules";
import type { CrmDeal } from "../hooks/useBatchModules";
import {
  adjustedProb, idleDecay, daysIdle, isOpen, isOverdue, bookWinRate,
  useDealActivities, useMoveStage, useAddDealNote,
  useStageRates, stageBase, type StageRates,
} from "../hooks/useCrmDirect";
import { useRole, useMe } from "../lib/authSupabase";
import { useTableSearch, SearchBar } from "../components/TableSearch";
import { ModuleProposals } from "../components/ModuleProposals";
import CaptureDeal from "../components/CaptureDeal";
import { exportCrmWorkbook } from "../lib/exportCrm";
import { fmtMoney as inr, fmtDate } from "../lib/money";


const STAGES = ["lead", "qualified", "proposal", "negotiation", "won", "lost"];
const STAGE_COLOR: Record<string, string> = {
  lead: "var(--fg-muted)", qualified: "var(--accent-purple)", proposal: "var(--accent-purple)",
  negotiation: "var(--status-warning)", won: "var(--status-good)", lost: "var(--status-critical)",
};
const probColor = (p: number) => (p >= 0.6 ? "var(--status-good)" : p >= 0.35 ? "var(--status-warning)" : "var(--status-critical)");

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "10px 8px" };
const numTd: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div title={hint}>
      <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>{label}</div>
      <div style={{ font: "600 20px Inter" }}>{value}</div>
    </div>
  );
}

// idle-adjusted win-probability chip; hover shows the derivation.
function WinChip({ d, rates }: { d: CrmDeal; rates?: StageRates }) {
  const p = adjustedProb(d, rates);
  const pct = Math.round(p * 100);
  if (d.stage === "won" || d.stage === "lost") {
    return <span style={{ fontSize: 12, color: d.stage === "won" ? "var(--status-good)" : "var(--status-critical)" }}>{d.stage}</span>;
  }
  const b = stageBase(d, rates);
  const idle = daysIdle(d.last_activity_at);
  const decay = idleDecay(d.last_activity_at);
  const baseLabel = b.source === "empirical"
    ? `Empirical ${Math.round(b.value * 100)}% (n=${b.n} decided reached ${d.stage})`
    : `Modelled ${Math.round(b.value * 100)}% (stage ${d.stage})`;
  const title =
    `${baseLabel} · idle ${idle}d → ×${decay.toFixed(2)} → ${pct}% adjusted` +
    (decay < 1 ? "  (discounted for staleness)" : "");
  return (
    <span title={title}
      style={{ fontSize: 12, fontWeight: 600, color: probColor(p), cursor: "help",
        borderBottom: "1px dotted currentColor" }}>
      {pct}%
    </span>
  );
}

function Flags({ d }: { d: CrmDeal }) {
  return (
    <>
      {isOverdue(d) && <span style={{ marginLeft: 6, fontSize: 10.5, color: "var(--status-critical)", border: "1px solid var(--status-critical)", borderRadius: 6, padding: "1px 5px" }}>overdue</span>}
      {isOpen(d) && daysIdle(d.last_activity_at) > 120 && (
        <span style={{ marginLeft: 6, fontSize: 10.5, color: "var(--status-warning)", border: "1px solid var(--status-warning)", borderRadius: 6, padding: "1px 5px" }}>idle {daysIdle(d.last_activity_at)}d</span>
      )}
    </>
  );
}

// ---- Kanban board ----------------------------------------------------------
function Board({ deals, onOpen, rates }: { deals: CrmDeal[]; onOpen: (d: CrmDeal) => void; rates?: StageRates }) {
  const byStage = useMemo(() => {
    const m = new Map<string, CrmDeal[]>();
    STAGES.forEach((s) => m.set(s, []));
    deals.forEach((d) => (m.get(d.stage) ?? m.set(d.stage, []).get(d.stage)!).push(d));
    return m;
  }, [deals]);
  return (
    <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8 }}>
      {STAGES.map((s) => {
        const list = byStage.get(s) ?? [];
        const value = list.reduce((sum, d) => sum + Number(d.amount), 0);
        return (
          <div key={s} style={{ minWidth: 190, flex: "1 0 190px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ font: "600 12px Inter", color: STAGE_COLOR[s], textTransform: "capitalize" }}>{s}</span>
              <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>{list.length} · {inr(value)}</span>
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {list.map((d) => (
                <div key={d.id} onClick={() => onOpen(d)}
                  style={{ border: "1px solid var(--border)", borderLeft: `3px solid ${STAGE_COLOR[s]}`, borderRadius: 8, padding: "9px 10px", background: "var(--surface)", cursor: "pointer" }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{d.deal_name}</div>
                  <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>{d.customer_name}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                    <span style={{ fontSize: 13 }}>{inr(d.amount)}</span>
                    <WinChip d={d} rates={rates} />
                  </div>
                  <Flags d={d} />
                </div>
              ))}
              {list.length === 0 && <div style={{ color: "var(--fg-faint)", fontSize: 12 }}>—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Deal drawer -----------------------------------------------------------
function DealDrawer({ deal, actor, canWrite, rates, onClose }: {
  deal: CrmDeal; actor: string; canWrite: boolean; rates?: StageRates; onClose: () => void;
}) {
  const activities = useDealActivities(deal.id);
  const move = useMoveStage();
  const addNote = useAddDealNote();
  const [note, setNote] = useState("");
  const [lostOpen, setLostOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [comp, setComp] = useState("");

  const confirmLost = () => {
    // competitor: use the field, else infer one from the reason ("lost to X",
    // "chose X", "went with X", "vs X") — generic, no competitor catalogue.
    const inferred = comp.trim() ||
      (reason.match(/(?:lost to|chose|went with|selected|picked|vs\.?)\s+([A-Z][\w&.]*(?:\s+[A-Z][\w&.]*){0,2})/)?.[1] ?? "");
    move.mutate(
      { deal, stage: "lost", actor, lossReason: reason, competitor: inferred },
      { onSuccess: () => { setLostOpen(false); setReason(""); setComp(""); } },
    );
  };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(8,9,12,.28)", zIndex: 50, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 420, maxWidth: "92vw", height: "100%", background: "var(--surface)", boxShadow: "-8px 0 24px rgba(0,0,0,.12)", padding: "20px 22px", overflowY: "auto", font: "14px Inter" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ font: "600 17px Inter" }}>{deal.deal_name}</div>
            <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>{deal.customer_name}</div>
          </div>
          <button onClick={onClose} style={{ border: 0, background: "none", fontSize: 20, color: "var(--fg-muted)", cursor: "pointer" }}>×</button>
        </div>

        <div style={{ display: "flex", gap: 20, margin: "16px 0", flexWrap: "wrap" }}>
          <Stat label="Amount" value={inr(deal.amount)} />
          <Stat label="Win (adjusted)" value={`${Math.round(adjustedProb(deal, rates) * 100)}%`}
            hint="Empirical (or modelled) stage win rate, discounted for how long the deal has been idle." />
          <Stat label="Expected close" value={deal.expected_close ?? "—"} />
        </div>

        {canWrite && isOpen(deal) && (
          <div style={{ margin: "12px 0" }}>
            <div style={{ fontSize: 12, color: "var(--fg-muted)", marginBottom: 4 }}>Move stage</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {STAGES.filter((s) => s !== deal.stage).map((s) => (
                <button key={s} disabled={move.isPending}
                  onClick={() => (s === "lost" ? setLostOpen(true) : move.mutate({ deal, stage: s, actor }))}
                  style={{ padding: "5px 10px", fontSize: 12, border: `1px solid ${STAGE_COLOR[s]}`, color: STAGE_COLOR[s], background: "none", borderRadius: 7, cursor: "pointer", textTransform: "capitalize" }}>
                  → {s}
                </button>
              ))}
            </div>
            {lostOpen && (
              <div style={{ marginTop: 10, padding: "10px 12px", border: "1px solid var(--surface-2)", background: "var(--surface-2)", borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: "var(--fg-muted)", marginBottom: 6 }}>Why was this lost?</div>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. price, timing, lost to a competitor…)"
                  style={{ width: "100%", padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, marginBottom: 6, boxSizing: "border-box" }} />
                <input value={comp} onChange={(e) => setComp(e.target.value)} placeholder="Competitor (optional)"
                  style={{ width: "100%", padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, marginBottom: 8, boxSizing: "border-box" }} />
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={confirmLost} disabled={move.isPending}
                    style={{ padding: "6px 12px", background: STAGE_COLOR.lost, color: "#fff", border: 0, borderRadius: 7, cursor: "pointer", fontSize: 13 }}>
                    Mark lost
                  </button>
                  <button onClick={() => setLostOpen(false)}
                    style={{ padding: "6px 12px", background: "none", border: "1px solid var(--border)", borderRadius: 7, cursor: "pointer", fontSize: 13, color: "var(--fg-muted)" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {deal.stage === "lost" && (deal.loss_reason || deal.competitor) && (
          <div style={{ margin: "12px 0", fontSize: 13, padding: "8px 11px", background: "var(--surface-2)", border: "1px solid var(--surface-2)", borderRadius: 8 }}>
            {deal.loss_reason && <div><span style={{ color: "var(--fg-muted)" }}>Lost — </span>{deal.loss_reason}</div>}
            {deal.competitor && <div style={{ marginTop: 2 }}><span style={{ color: "var(--fg-muted)" }}>Competitor: </span>{deal.competitor}</div>}
          </div>
        )}

        {deal.next_step && (
          <div style={{ margin: "12px 0", fontSize: 13 }}>
            <span style={{ color: "var(--fg-muted)" }}>Next step: </span>{deal.next_step}
          </div>
        )}

        <div style={{ font: "600 13px Inter", margin: "18px 0 8px" }}>Activity</div>
        {canWrite && (
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…"
              style={{ flex: 1, padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13 }} />
            <button disabled={!note.trim() || addNote.isPending}
              onClick={() => addNote.mutate({ dealId: deal.id, text: note.trim(), actor }, { onSuccess: () => setNote("") })}
              style={{ padding: "6px 12px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
              Add
            </button>
          </div>
        )}
        {activities.isLoading ? <div style={{ color: "var(--fg-muted)" }}>loading…</div> : (
          <div style={{ display: "grid", gap: 8 }}>
            {(activities.data ?? []).map((a) => (
              <div key={a.id} style={{ borderLeft: "2px solid var(--border)", paddingLeft: 10 }}>
                <div style={{ fontSize: 13 }}>{a.summary}</div>
                <div style={{ color: "var(--fg-muted)", fontSize: 11 }}>
                  {a.kind} · {a.actor_name ?? "—"} · {fmtDate(a.occurred_at)}
                </div>
              </div>
            ))}
            {(activities.data ?? []).length === 0 && <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>No activity yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- loss analysis ---------------------------------------------------------
// Why deals are lost, aggregated from Rasvyn's own book: lost count + value by
// reason and by competitor. Ported mechanism; no THB competitor catalogue.
function tally(rows: CrmDeal[], key: (d: CrmDeal) => string | null) {
  const m = new Map<string, { count: number; value: number }>();
  for (const d of rows) {
    const k = key(d)?.trim();
    if (!k) continue;
    const e = m.get(k) ?? { count: 0, value: 0 };
    e.count += 1; e.value += Number(d.amount) || 0;
    m.set(k, e);
  }
  return [...m.entries()].map(([k, v]) => ({ k, ...v })).sort((a, b) => b.value - a.value);
}

function LossAnalysis({ deals }: { deals: CrmDeal[] }) {
  const lost = useMemo(() => deals.filter((d) => d.stage === "lost"), [deals]);
  const byReason = useMemo(() => tally(lost, (d) => d.loss_reason), [lost]);
  const byComp = useMemo(() => tally(lost, (d) => d.competitor), [lost]);
  if (lost.length === 0) return null;

  const lostValue = lost.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const labelled = lost.filter((d) => d.loss_reason || d.competitor).length;

  const Col = ({ title, rows, empty }: { title: string; rows: { k: string; count: number; value: number }[]; empty: string }) => (
    <div style={{ flex: 1, minWidth: 220 }}>
      <div style={{ fontSize: 12, color: "var(--fg-muted)", marginBottom: 6 }}>{title}</div>
      {rows.length === 0 ? <div style={{ fontSize: 13, color: "var(--fg-faint)" }}>{empty}</div> : (
        <div style={{ display: "grid", gap: 5 }}>
          {rows.slice(0, 6).map((r) => (
            <div key={r.k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, gap: 10 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.k}</span>
              <span style={{ color: "var(--fg-muted)", whiteSpace: "nowrap" }}>{r.count} · {inr(r.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <details className="sanctuary-card" style={{ padding: "10px 14px", marginBottom: 22 }}>
      <summary style={{ cursor: "pointer", font: "600 13px Inter" }}>
        Loss analysis <span style={{ color: "var(--fg-muted)", fontWeight: 400 }}>· {lost.length} lost · {inr(lostValue)} · {labelled}/{lost.length} labelled</span>
      </summary>
      <div style={{ display: "flex", gap: 28, marginTop: 12, flexWrap: "wrap" }}>
        <Col title="By reason" rows={byReason} empty="No reasons recorded yet." />
        <Col title="By competitor" rows={byComp} empty="No competitors recorded yet." />
      </div>
    </details>
  );
}

// ---- panel -----------------------------------------------------------------
export function CrmPanel() {
  const { data, isLoading, error } = useCrmDeals();
  const { data: rates } = useStageRates();
  const { canWrite } = useRole();
  const me = useMe(true);
  const [view, setView] = useState<"board" | "table">("board");
  const [openDeal, setOpenDeal] = useState<CrmDeal | null>(null);
  const [exporting, setExporting] = useState(false);
  const { q, setQ, filtered } = useTableSearch(data, (r) => `${r.deal_name} ${r.customer_name} ${r.owner_name ?? ""}`);

  const exportXlsx = async () => {
    setExporting(true);
    try { await exportCrmWorkbook(data ?? [], rates); }
    finally { setExporting(false); }
  };

  const k = useMemo(() => {
    const list = data ?? [];
    const open = list.filter(isOpen);
    return {
      weightedAdj: open.reduce((s, d) => s + Number(d.amount) * adjustedProb(d, rates), 0),
      weightedNaive: open.reduce((s, d) => s + Number(d.weighted_value), 0),
      winRate: bookWinRate(list),
      atRisk: open.filter((d) => isOverdue(d) || daysIdle(d.last_activity_at) > 120).length,
      openCount: open.length,
    };
  }, [data, rates]);

  if (isLoading) return <p style={{ padding: 24 }}>Loading deals…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;

  const actor = me?.full_name ?? "system";

  return (
    <div>
      <div style={{ display: "flex", gap: 26, margin: "4px 0 20px", flexWrap: "wrap" }}>
        <Stat label="Weighted pipeline" value={inr(k.weightedAdj)}
          hint="Idle-adjusted: each open deal's amount × win probability, discounted for staleness." />
        <Stat label="Unadjusted" value={inr(k.weightedNaive)} hint="Amount × stage win probability, before the idle discount." />
        <Stat label="Win rate" value={k.winRate == null ? "—" : `${Math.round(k.winRate * 100)}%`} hint="Won ÷ (won + lost) across the book." />
        <Stat label="Open" value={String(k.openCount)} />
        <Stat label="At-risk" value={String(k.atRisk)} hint="Open deals that are overdue or idle 120+ days." />
      </div>

      {rates && rates.size > 0 && (
        <div style={{ display: "flex", gap: 14, alignItems: "center", margin: "0 0 18px", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>Empirical stage win rate:</span>
          {["lead", "qualified", "proposal", "negotiation"].map((s) => {
            const r = rates.get(s);
            const trusted = r && r.rate != null && r.decided >= 2;
            return (
              <span key={s} title={r ? `reached ${r.reached}, decided ${r.decided}, won rate ${r.rate == null ? "—" : Math.round(r.rate * 100) + "%"}` : ""}
                style={{ fontSize: 12, textTransform: "capitalize", color: trusted ? "var(--fg)" : "var(--fg-faint)" }}>
                {s} <b style={{ color: trusted ? STAGE_COLOR[s] : "var(--fg-faint)" }}>
                  {r && r.rate != null ? `${Math.round(r.rate * 100)}%` : "—"}
                </b>
                <span style={{ color: "var(--fg-faint)" }}> (n={r?.decided ?? 0})</span>
              </span>
            );
          })}
        </div>
      )}

      <LossAnalysis deals={data ?? []} />

      {canWrite && <CaptureDeal customers={(data ?? []).map((d) => d.customer_name)} />}

      <ModuleProposals table="crm_proposals" />

      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "0 0 14px" }}>
        {(["board", "table"] as const).map((v) => (
          <button key={v} onClick={() => setView(v)}
            style={{ padding: "5px 12px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 7, cursor: "pointer",
              background: view === v ? "var(--accent-purple)" : "none", color: view === v ? "#fff" : "var(--fg-muted)", textTransform: "capitalize" }}>
            {v}
          </button>
        ))}
        {view === "table" && <SearchBar q={q} setQ={setQ} count={filtered.length} total={(data ?? []).length} placeholder="Search deal / customer / owner…" />}
        <button onClick={exportXlsx} disabled={exporting || (data ?? []).length === 0}
          title="Download the CRM book as a multi-sheet Excel file (deals, stage rates, losses)."
          style={{ marginLeft: "auto", padding: "5px 12px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 7, cursor: "pointer", background: "none", color: "var(--fg-muted)" }}>
          {exporting ? "Exporting…" : "Export Excel"}
        </button>
      </div>

      {view === "board" ? (
        <Board deals={data ?? []} onOpen={setOpenDeal} rates={rates} />
      ) : (
        <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
              <th style={th}>Deal</th><th style={th}>Customer</th><th style={th}>Stage</th>
              <th style={{ ...th, textAlign: "right" }}>Amount</th><th style={th}>Win</th><th style={th}>Owner</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id} onClick={() => setOpenDeal(d)} style={{ borderTop: "1px solid var(--border)", cursor: "pointer" }}>
                <td style={td}>{d.deal_name}<Flags d={d} /></td>
                <td style={td}>{d.customer_name}</td>
                <td style={{ ...td, color: STAGE_COLOR[d.stage], textTransform: "capitalize" }}>{d.stage}</td>
                <td style={numTd}>{inr(d.amount)}</td>
                <td style={td}><WinChip d={d} rates={rates} /></td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{d.owner_name ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {openDeal && <DealDrawer deal={openDeal} actor={actor} canWrite={canWrite} rates={rates} onClose={() => setOpenDeal(null)} />}
    </div>
  );
}
